import { FC, useState } from "react";
import { Produit } from "../../lib/interfaces/produit.interface";
import Box from "../ui/Box";
import Title from "../ui/Title";
import { Alert } from "../ui/alert";
import { Check, Download, EuroIcon, InfoIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ModeDePaiement } from "../../lib/enums/mode-de-paiement.enum";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ksAchatsQK } from "../../lib/query-keys";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import axiosClient from "../../lib/axiosClient";
import { Facture } from "../../lib/interfaces/facture.interface";
import { useNavigate } from "react-router-dom";

interface Props {
  produit: Produit;
  hasFactureEnAttente: boolean;
  isProductBought: boolean;
  isDisabled: boolean;
}

const ProduitItem: FC<Props> = ({ hasFactureEnAttente, isProductBought, produit, isDisabled }) => {
  const [modeDePaiement, setModeDePaiement] = useState<ModeDePaiement | "">("");
  const [termesAcceptes, setTermesAcceptes] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const redirectToCheckout = async () => {
    const { data: stripeCheckoutSessionId } = await axiosClient.post<string>(
      `/paiement/creer-stripe-checkout-session-pour-produit-digital`,
      { productId: produit.id }
    );
    const stripe = await loadStripe(`${import.meta.env.VITE_SNA_STRIPE_KEY}`);
    stripe?.redirectToCheckout({ sessionId: stripeCheckoutSessionId });
  };

  const payerOffline = async () => {
    const { data: facture } = await axiosClient.post<Facture>("/produits/facture", {
      modeDePaiement,
      productId: produit.id,
    });
    navigate(`/boutique/confirmation-achat?id=${facture.id}`);
  };

  const { mutate: handleSubmit, isPending: loading } = useMutation({
    mutationFn: async () => {
      if (modeDePaiement === ModeDePaiement.CB) {
        await redirectToCheckout();
      } else {
        await payerOffline();
      }
    },
    onSuccess: () => {},
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [ksAchatsQK] });
    },
    onError: (err: any) => {
      console.error(err.response?.data || err);
      toast.error(`Une erreur s'est produite`);
    },
  });

  const boutonAAfficher = () => {
    // Facture à payer
    if (hasFactureEnAttente) {
      return <Alert className='bg-gray-50'>En attente du paiement</Alert>;
    }

    // Produit acheté
    if (isProductBought && produit.digital && produit.url) {
      return (
        <div className='flex flex-col gap-3'>
          <div className='mb-1 flex gap-2'>
            <Check className='text-green-500' />
            <div>Produit acheté</div>
          </div>
          <a
            className='duration-200 flex text-sm font-medium gap-3 bg-purple-800 text-white py-2 px-4 rounded-md hover:bg-purple-700 w-min'
            href={produit.url}
            target='_blank'
          >
            <Download className='w-5 h-5' />
            Télécharger
          </a>
        </div>
      );
    }

    if (isDisabled && !isProductBought) {
      return (
        <div className='text-red-400 flex gap-3'>
          <InfoIcon />
          La souscription d'un pack est nécessaire pour accéder à l'achat. Si votre abonnement est en attente de
          paiement, veuillez en régulariser la situation pour débloquer cet accès.
        </div>
      );
    }

    // Peut acheter
    return (
      <Dialog
        onOpenChange={() => {
          setTermesAcceptes(false);
          setModeDePaiement("");
        }}
      >
        <DialogTrigger asChild>
          <Button icon={<EuroIcon className='w-5 h-5' />}>Acheter</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mode de paiement</DialogTitle>
            <DialogDescription>Veuillez choisir votre mode de paiement.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* Radio buttons */}
            <RadioGroup
              disabled={loading}
              value={modeDePaiement}
              onValueChange={(val: ModeDePaiement) => setModeDePaiement(val)}
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value={ModeDePaiement.CB} id='cb' />
                <Label htmlFor='cb'>Carte bancaire</Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value={ModeDePaiement.VIR} id='vir' />
                <Label htmlFor='vir'>Virement</Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value={ModeDePaiement.CHQ} id='chq' />
                <Label htmlFor='chq'>Chèque</Label>
              </div>
            </RadioGroup>
            {/* Checkbox */}
            <div className='flex items-center space-x-3 mt-5'>
              <Checkbox
                id='terms'
                disabled={loading}
                checked={termesAcceptes}
                onCheckedChange={(val: boolean) => setTermesAcceptes(val)}
              />
              <label
                htmlFor='terms'
                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
              >
                Pour profiter de mes étiquettes dès réception du paiement, je renonce expressément à mon délai de
                rétractation de 14 jours.
              </label>
            </div>

            <DialogFooter className='mt-7'>
              <Button type='submit' loading={loading} disabled={!modeDePaiement || !termesAcceptes}>
                Commander et s'engager à payer {(produit.prix / 100).toFixed(2)}€
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Box className='flex flex-col gap-2'>
      <Title order={3} className='mb-4'>
        {produit.nom}
      </Title>
      <div className='flex flex-col gap-5'>
        {produit.imageUrl && <img src={produit.imageUrl} alt='' width={450} height={129} />}
        <div className='flex flex-col gap-2'>
          <p>
            Afin d'aider le plus grand nombre d'apiculteurs à se mettre en conformité avec la nouvelle réglementation de
            signalétique info-tri, nous proposons à la vente, à nos abonnés à l'Abeille de France et aux adhérents des
            structures départementales affiliées au SNA, 2 types d'étiquettes déjà conçues pour les bocaux en verre et
            les pots en plastique sous format pdf et jpeg.
          </p>
          <p>
            Vous pourrez ainsi facilement intégrer cette nouvelle signalétique à vos étiquettes ou bien les imprimer
            seules et les apposer en complément sur vos pots.
          </p>
          <p>
            Vous trouverez également le Guide de la nouvelle signalétique de tri des emballages ménagers qui détaille
            entre autres les formats minimums de ces étiquettes.
          </p>
          <p>
            Pour plus d'informations concernant la nouvelle signalétique Info-tri à apposer à partir du 9 mars 2023 nous
            vous invitons à consulter l'article correspondant:
            <br />
            <a
              href='https://www.snapiculture.com/info-tri-citeo-a-appliquer-avant-le-9-mars-2023-emballages-menagers-et-papiers-graphiques/'
              target='_blank'
              rel='noreferrer'
              className='text-orange-600 hover:text-orange-500 duration-300 font-medium mt-1 block'
            >
              Consignes à appliquer avant le 9 mars 2023
            </a>
          </p>
        </div>
        {!isProductBought && (
          <div>
            <p className='font-semibold px-3 py-1 rounded-md bg-gray-200 inline-block'>
              {(produit.prix / 100).toFixed(2)} €
            </p>
          </div>
        )}
        <div>{boutonAAfficher()}</div>
      </div>
    </Box>
  );
};

export default ProduitItem;
